/* @flow */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Paper from '@material-ui/core/Paper';

//----------------------------------------------------------------------//
// GLOBAL COMPONENTS                                                    //
//----------------------------------------------------------------------//

import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import ActionsList from 'components/ActionsList/ActionsList';
import Activity from 'components/Activity/Activity';
import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';
import Loading from 'components/Loading/Loading';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import PageTitle from 'components/PageTitleLegacy/PageTitle';
import CompanyAssetsTable from 'components/Locations/CompanyAssetsTable';
import CountriesSitesExpandableList from 'components/Locations/CountriesSitesExpandableList/CountriesSitesExpandableList';
import UsersRoleExpandableList from 'components/User/UsersRoleExpandableList';

//----------------------------------------------------------------------//
// LOCAL COMPONENTS                                                     //
//----------------------------------------------------------------------//

import type { TypeActionDrawerStore } from 'stores/ActionDrawerStore';

//----------------------------------------------------------------------//
// CSS                                                                  //
//----------------------------------------------------------------------//

import styles from 'common-scss/DetailsPage.module.scss';

import actionsListData from './actionsListData';
import { generateUrl, company } from 'api/routes';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

@translate()
@inject('navigationStore', 'accountStore', 'actionDrawerStore')
@observer
export default class CompanyDetailsPage extends React.Component<*> {
  componentDidMount() {
    const accountStore: Object = this.props.accountStore;
    const accountId: string = this.props.match.params.id;
    const companyId: string = this.props.match.params.companyId;

    if (accountStore.accountId !== accountId) {
      accountStore.loadDetails(accountId);
    }

    if (!accountStore.companyDetails || accountStore.companyDetails.id !== companyId) {
      accountStore.loadCompanyDetails(companyId);
    }

    this.props.navigationStore.pushPath(0, '/accounts');
    this.props.navigationStore.pushPath(1, `/accounts/${accountId}`, 0);
    this.props.navigationStore.pushPath(2, `/accounts/${accountId}`, 2);
  }

  componentWillUnmount() {
    const actionDrawerStore: TypeActionDrawerStore = this.props.actionDrawerStore;
    actionDrawerStore.clearActionDrawer();
  }

  render(): React.Element<*> {
    const { companyDetails }: Object = this.props.accountStore;

    const accountId: string = this.props.match.params.id;
    const companyId: string = this.props.match.params.companyId;
    const loadingResources: boolean = this.props.accountStore.loadingResources;
    const details: Object = this.props.accountStore.details;

    const t: Function = this.props.t;

    if (loadingResources || !details || !companyDetails) {
      return <Loading />;
    }

    const titleEnd: string = t('general:company');

    const title: string = `
            ${t('general:trading_account_plural')} |
            ${details.details.name} |
            ${t('general:actions.manage_location_plural')} |
            ${companyDetails.name} (${titleEnd})`;

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <PageTitle title={title} />

          <div className={styles['grid-wrapper']}>
            <Paper className={styles['feature-item']}>
              <div className={styles['details-panel']}>
                <CountriesSitesExpandableList
                  title={t('general:locations.labels.company_locations')}
                  details={companyDetails}
                  accountId={accountId}
                  companyId={companyId}
                />

                <CompanyAssetsTable
                  title={t('general:locations.assigned_to_company')}
                  companyId={companyDetails.id}
                  assetsCount={companyDetails.assets_count}
                />

                <UsersRoleExpandableList
                  title={t('general:locations.assigned_to_company')}
                  details={companyDetails}
                  companyId={companyId}
                  accountId={accountId}
                />
              </div>

              <div className={styles['actions-panel']}>
                <StatusIndicator title={t('general:locations.labels.status')} status="active" />
                <ActionsList
                  actions={actionsListData}
                  data={{
                    details: details.details,
                    locationDetails: companyDetails,
                    companyDetails,
                    accountId: details.details.id,
                    locationId: companyDetails.id,
                    titleEnd,
                  }}
                />
              </div>
            </Paper>

            <Paper className={styles['activity-panel']}>
              {this.props.match.params.companyId && (
                <Activity
                  title={true}
                  url={generateUrl(company.activity, [this.props.match.params.companyId])}
                  refresh={this.props.actionDrawerStore.success}
                  allowFilter={true}
                />
              )}
            </Paper>
          </div>
        </PageWrapper>

        <ActionDrawerWrapper />
      </React.Fragment>
    );
  }
}
